import {
  format as formatDateViaFNS,
  formatDistanceToNow,
  isToday,
  differenceInHours,
  isSameDay,
} from 'date-fns'
import {toZonedTime, fromZonedTime, formatInTimeZone} from 'date-fns-tz'
import {enIN} from 'date-fns/locale'

import i18next from 'i18next'
import jwtDecode from 'jwt-decode'
import {dispatch} from 'app/store'
import Toast from 'components/Basic/Toast'
import {
  matchBracketsOnEdgesRegEx,
  DEFAULT_DATE_FORMAT,
  DATE_AT_TIME_12H_FORMAT,
} from 'definitions/constant'
import {policiesActions} from 'features/Policies/policiesSlice'
import {
  AUTH_PROVIDER_DISPLAY_VALUE,
  CreatedDateItem,
  CheckValidUrlOrIPAddress,
  ExtractDomainOrIPAddress,
  TokenDataType,
  CACServices,
} from 'interfaces'
import {EndPoints} from 'services/api/HTTP/HTTP.types'
import {checkDomainAgainstCAC} from 'features/Policies/checkCACDomainHelpers'
import {history} from './history'
import {checkWildcardWithTLD} from './regex'

// URL regex parts
const protocolRegex = /^(https?:\/\/)?/
const subdomainNameRegex = /(\*?\.?[a-zA-Z0-9-]+)\.(?:[a-zA-Z0-9-]+\.)*/
const domainNameRegex = /(?:(?:xn--\w+|[a-zA-Z]{2,24})\b)/
const pathRegex = /(\/[^\s]*)?/
const noTrailingDotRegex = /(?!\.)$/
const portRegex = /(?::\d+)?/ // Optional port number

// ip v4 and v6 conditions
// source - ip-regex v5.0.0 npm
const ipV4 =
  '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)(?:\\.(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)){3}'
const ipV6Segment = '[a-fA-F\\d]{1,4}'
const ipV6 = `
(?:
(?:${ipV6Segment}:){7}(?:${ipV6Segment}|:)|                                    // 1:2:3:4:5:6:7::  1:2:3:4:5:6:7:8
(?:${ipV6Segment}:){6}(?:${ipV4}|:${ipV6Segment}|:)|                             // 1:2:3:4:5:6::    1:2:3:4:5:6::8   1:2:3:4:5:6::8  1:2:3:4:5:6::1.2.3.4
(?:${ipV6Segment}:){5}(?::${ipV4}|(?::${ipV6Segment}){1,2}|:)|                   // 1:2:3:4:5::      1:2:3:4:5::7:8   1:2:3:4:5::8    1:2:3:4:5::7:1.2.3.4
(?:${ipV6Segment}:){4}(?:(?::${ipV6Segment}){0,1}:${ipV4}|(?::${ipV6Segment}){1,3}|:)| // 1:2:3:4::        1:2:3:4::6:7:8   1:2:3:4::8      1:2:3:4::6:7:1.2.3.4
(?:${ipV6Segment}:){3}(?:(?::${ipV6Segment}){0,2}:${ipV4}|(?::${ipV6Segment}){1,4}|:)| // 1:2:3::          1:2:3::5:6:7:8   1:2:3::8        1:2:3::5:6:7:1.2.3.4
(?:${ipV6Segment}:){2}(?:(?::${ipV6Segment}){0,3}:${ipV4}|(?::${ipV6Segment}){1,5}|:)| // 1:2::            1:2::4:5:6:7:8   1:2::8          1:2::4:5:6:7:1.2.3.4
(?:${ipV6Segment}:){1}(?:(?::${ipV6Segment}){0,4}:${ipV4}|(?::${ipV6Segment}){1,6}|:)| // 1::              1::3:4:5:6:7:8   1::8            1::3:4:5:6:7:1.2.3.4
(?::(?:(?::${ipV6Segment}){0,5}:${ipV4}|(?::${ipV6Segment}){1,7}|:))             // ::2:3:4:5:6:7:8  ::2:3:4:5:6:7:8  ::8             ::1.2.3.4
)(?:%[0-9a-zA-Z]{1,})?                                             // %eth0            %1
`
  .replace(/\s*\/\/.*$/gm, '')
  .replace(/\n/g, '')
  .trim()

// Pre-compile only the exact regExes because adding a global flag make regExes stateful
const ipV4V6ExactRegExWithProtocolCheck = new RegExp(
  `^(https?://)?(?:${ipV4}|${ipV6})(?:/|$)(?!/)`,
)
const ipV4ExactRegExWithProtocolCheck = new RegExp(
  `^(https?://)?(?:${ipV4})(?:/|$)(?!/)`,
)

const ipV4V6ExactRegEx = new RegExp(`(?:^${ipV4}$)|(?:^${ipV6}$)`)
const ipV4ExactRegEx = new RegExp(`^${ipV4}$`)
const ipV6WithBracketsRegEx = new RegExp(String.raw`^\[?${ipV6}\]?$`)

/* eslint-disable @typescript-eslint/no-explicit-any */
export const unwrapAPIError = (error: any): string => {
  let errorValue = error?.data || error?.statusText || error?.response

  if (errorValue?.data && typeof errorValue?.data === 'object') {
    // handle new error schema if present.
    // new schema returns list of errors.
    if (errorValue.data.code) {
      // Prefer the error object's custom `code` property when available
      errorValue = errorValue.data.code
    } else if (
      Array.isArray(errorValue.data.errors) &&
      errorValue.data.errors.length
    ) {
      const errorNode = errorValue.data.errors[0]
      errorValue = errorNode.message || errorNode.details
    } else {
      errorValue = errorValue.data.message || errorValue.data[0]?.msg
    }
  } else if (!error?.data && error?.message) {
    errorValue = error.message
  } else if (errorValue?.message) {
    errorValue = errorValue.message
  } else if (errorValue?.[0] && errorValue?.[0]?.message) {
    errorValue = errorValue[0].message
  }

  if (
    error?.config?.url === EndPoints.RefreshToken ||
    errorValue?.[0]?.msg === 'Invalid token.' ||
    errorValue?.[0]?.msg === 'Network Error' ||
    errorValue?.[0]?.msg === 'Forbidden' ||
    error?.response?.status === 500 ||
    error?.response?.status === 403 ||
    errorValue === 'Invalid token.' ||
    errorValue === 'Forbidden' ||
    errorValue === 'Request timeout' ||
    errorValue === 'Network Error'
  ) {
    return ''
  } else if (typeof errorValue === 'string') {
    return errorValue
  } else if (typeof errorValue === 'object') {
    return errorValue[0]?.msg ?? ''
  } else {
    return ''
  }
}

export const cacheBuster = (val: string) => `${val}?cb=${Date.now()}`

export function formatDate(date: string) {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return (
    [month, day, year].join('-') +
    ' at ' +
    d.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
  )
}

export const queryParser = <
  T extends Record<string, string | undefined> = Record<string, string>,
>(
  queryString: string,
): Partial<T> => {
  const query: Record<string, string> = {}
  const pairs = (
    queryString.startsWith('?') ? queryString.slice(1) : queryString
  ).split('&')
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split('=')
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '')
  }
  return query as Partial<T>
}

export const componentLoader = (
  lazyComponent: () => any,
  attemptsLeft: number = 3,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    lazyComponent()
      .then(resolve)
      .catch((error: any) => {
        // let us retry after 1500 ms
        setTimeout(() => {
          if (attemptsLeft === 1) {
            reject(error)
            return
          }
          componentLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject)
        }, 300)
      })
  })
}

export const copyToClipboard = async ({textToCopy}: {textToCopy: string}) => {
  try {
    const textField = document.createElement('textarea')
    textField.innerText = textToCopy
    document.body.appendChild(textField)
    textField.select()
    document.execCommand('copy')
    textField.remove()
  } catch (err) {
    //console.log(err)
  }
}

export const copyFormattedToClipboard = (text: string): void => {
  // IE 11 does not support clipboard writeText()
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => {
        Toast({message: 'Copied!', type: 'success'})
      },
      err => {
        console.error('Async: Could not copy text: ', err)
        Toast({message: 'Failed to copy!', type: 'error'})
      },
    )
  } else {
    copyToClipboard({
      textToCopy: text,
    })
    Toast({message: 'Copied!', type: 'success'})
  }
}

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const validateEmail = (email: any) => {
  return emailRegex.test(String(email).toLowerCase())
}

export const validateEmails = (emailInput: string): boolean => {
  const regex =
    /^(\w+((-\w+)|(\.\w+))*\@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z0-9]{2,10}\s*?,?\s*?)+$/ // eslint-disable-line

  return regex.test(emailInput)
}

export const removeDuplicateObjectsFromArray = <T>(
  data: T[], // here we use a generic type 'T' to extract the type from the usage of this method
  key: (e: T) => string,
) => {
  // based on the usage, we now know what type this data is and now also what will be returned
  return [...new Map(data.map(x => [key(x), x])).values()]
}

export const deleteKey = (obj: any, path: string, elmToDel: string) => {
  const _obj = JSON.parse(JSON.stringify(obj))
  const keys = path.split('.')
  keys.push(elmToDel)
  const traverseRecursivelyAndDelete = (
    acc: any,
    aKeys: string[],
    aElmToDel: string,
  ): void => {
    const key = aKeys.shift() || ''
    if (key === aElmToDel) {
      delete acc[key]
    } else {
      traverseRecursivelyAndDelete(acc[key], aKeys, aElmToDel)
    }
  }

  traverseRecursivelyAndDelete(_obj, keys, elmToDel)

  return _obj
}

const generateNewUrlRegExp = () => {
  return new RegExp(
    '^' + // Ensure we match from the start
      protocolRegex.source +
      subdomainNameRegex.source +
      domainNameRegex.source +
      portRegex.source +
      pathRegex.source +
      noTrailingDotRegex.source +
      '$', // Ensure we match the entire string
    'i', // Case insensitive matching
  )
}

export const regExp = {
  urlBypass:
    /* eslint-disable-next-line no-useless-escape */
    /[[-a-zA-Z0-9@:%_\+.~#?&//=]{1,256}\]*.[a-z]{1,24}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?/gi,
}

export function extractProtocolSubdomain(str: string) {
  const patterns = [
    'http://www.',
    'https://www.',
    'http://',
    'https://',
    'www.',
  ]
  for (let i = 0, len = patterns.length; i < len; i++) {
    const pattern = patterns[i]
    if (str.indexOf(pattern) == 0) return str.substring(pattern.length)
  }
  return str
}

export function numberWithCommas(x: number | string) {
  return x ? x.toLocaleString() : 0
}

export const formatBytes = (
  bytes: number,
  decimals = 2,
  spaceText?: string,
): string => {
  if (bytes === 0) return '0Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${
    spaceText || ''
  }${sizes[i]}`
}

export const isEmptyObject = (obj: any) => {
  return (
    obj &&
    Object.keys(obj).length === 0 &&
    Object.getPrototypeOf(obj) === Object.prototype
  )
}

export const formatTransactions = (labelValue: number = 0) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(labelValue)) >= 1.0e9
    ? Math.round(Math.abs(Number(labelValue)) / 1.0e9) + 'B'
    : // Six Zeroes for Millions
    Math.abs(Number(labelValue)) >= 1.0e6
    ? Math.round(Math.abs(Number(labelValue)) / 1.0e6) + 'M'
    : // Three Zeroes for Thousands
    Math.abs(Number(labelValue)) >= 1.0e3
    ? Math.round(Math.abs(Number(labelValue)) / 1.0e3) + 'K'
    : Math.abs(Number(labelValue))
}

export const getBrowserTimeZone = (): string => {
  return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || ''
}

const toUTCString = (dateString: string): string => {
  const date = new Date(dateString)
  const utcString = date.toISOString()
  return utcString
}

/**
 *
 * @param date string
 * @description Accepts a date string and returns a formatted date string in local timezone
 * @returns
 */
export const transformDateToLocal = (date: string) => {
  if (!date) return ''
  const UTCDateString = toUTCString(date)
  const timezone = getBrowserTimeZone()
  const localDate = toZonedTime(new Date(UTCDateString), timezone)

  return formatDateViaFNS(localDate, DATE_AT_TIME_12H_FORMAT)
}

/**
 * Format dates of past and future to "DD MMM YYYY"
 * and today's date with relative time ago
 * @param date string
 * @returns string
 */
export const transformDateToLocalWithSameDay = (date: string) => {
  if (!date) return ''

  const timeZone = getBrowserTimeZone()
  const originalDate = fromZonedTime(new Date(date), timeZone)
  const now = new Date()

  // Check the difference in hours
  const hoursDiff = differenceInHours(now, originalDate)

  // Check if it's the same day
  const isSameDay = isToday(originalDate)

  // If the difference is less than 24 hours and it's the same day, show relative time
  if (hoursDiff < 24 && isSameDay) {
    return formatDistanceToNow(originalDate, {addSuffix: true})
  } else {
    return (
      formatDateViaFNS(originalDate, 'dd MMM yyyy') +
      ' at ' +
      formatDateViaFNS(originalDate, 'hh:mm a')
    )
  }
}

/**
 * @param date string
 * @param format string, optional
 * @returns string
 */
export const localDateWithTimeZone = (
  date: string,
  format: string = 'MM/dd/yy hh:mm a z',
): string => {
  if (!date) return ''
  const timeZone = getBrowserTimeZone()
  const zonedDate = toZonedTime(date, timeZone)
  return formatInTimeZone(zonedDate, timeZone, format, {
    locale: enIN,
  })
}

export const checkIfSameDay = (d1: string, d2: string) => {
  // get the date in yyyy-MM-dd format
  const date1 = formatDateViaFNS(d1, DEFAULT_DATE_FORMAT)
  const date2 = formatDateViaFNS(d2, DEFAULT_DATE_FORMAT)
  return isSameDay(date1, date2)
}

// Do not allow # ! @ $ % ^ * ? . / \ in policy/custom category names.
export const policyRegex = /^[^#!@$%^*?./\\]+$/

export const goPolicyToRoute = (route: string, policyName?: string) => {
  dispatch(policiesActions.updatePolicyName(policyName || ''))
  history.push(route)
}

export const checkTextOverflowed = (event: any) => {
  const target = event.currentTarget
  const title = target.textContent || ''
  const horizontallyOverflowed = target.scrollWidth > target.clientWidth
  const verticallyOverflowed = target.scrollHeight > target.clientHeight
  target.title = horizontallyOverflowed || verticallyOverflowed ? title : ''
}

export const getAuthProviderDisplayName = (provider: string): string => {
  return (
    AUTH_PROVIDER_DISPLAY_VALUE[
      provider?.toLowerCase() as keyof typeof AUTH_PROVIDER_DISPLAY_VALUE
    ] || provider
  )
}

export const validateIPWithProtocol = (ip: string): boolean => {
  return ipV4V6ExactRegExWithProtocolCheck.test(ip)
}

export const validateIPv4WithProtocol = (ip: string): boolean => {
  return ipV4ExactRegExWithProtocolCheck.test(ip)
}

export const validateUrlWithProtocol = (url: string): boolean => {
  const urlStr = generateNewUrlRegExp()
  return urlStr.test(url)
}

/**
 * @params
 * url - this is either a URL or an IP address.
 */
export function extractDomainOrIP(
  url: string,
  withPath: boolean = true,
): ExtractDomainOrIPAddress {
  const withoutProtocol = url.trim().replace(/(https?:\/\/)/, '')
  const withoutTrailingSlash = withoutProtocol.replace(/\/$/, '')

  const parts = withoutTrailingSlash.split('/')
  const hasURLPath = parts.length > 1

  // Check if the result is an IPv6 address in square brackets and remove square brackets.
  if (
    withoutTrailingSlash.startsWith('[') ||
    withoutTrailingSlash.endsWith(']')
  ) {
    return {
      domain: withoutTrailingSlash.replace(matchBracketsOnEdgesRegEx, ''),
      hasPath: hasURLPath,
    }
  }
  if (withPath) {
    return {domain: withoutTrailingSlash, hasPath: hasURLPath}
  }
  return {domain: parts[0], hasPath: hasURLPath}
}

/**
 * @params
 * url - this is either a URL or an IP address.
 */
export const checkValidUrlOrIPAddress = (
  domain: string,
): CheckValidUrlOrIPAddress => {
  const url = domain.trim()
  const isUrlValid = validateUrlWithProtocol(url)
  const isWildcardFollowedByTLD = checkWildcardWithTLD(url)
  const trimmedURLWithoutEdgeBrackets = url
    .trim()
    .replace(matchBracketsOnEdgesRegEx, '')
  const isValidIPV4 = validateIPv4WithProtocol(trimmedURLWithoutEdgeBrackets)
  const isValidIP = validateIPWithProtocol(trimmedURLWithoutEdgeBrackets)
  return {
    isUrlValid: isUrlValid || isWildcardFollowedByTLD,
    isValidIP,
    isValidIPV4,
    // We want to allow url pattern *.com, *.in, etc...
    isValid: isUrlValid || isWildcardFollowedByTLD || isValidIP,
  }
}

export const validURLChecker = (url: string): boolean => {
  const urlStr = generateNewUrlRegExp()
  const isUrlValid = urlStr.test(url)
  const isValidIP = validateIP(
    url.trim().replace(matchBracketsOnEdgesRegEx, ''),
  )
  return isUrlValid || isValidIP
}

export const validateIP = (ip: string): boolean => {
  return ipV4V6ExactRegEx.test(ip)
}

/**
 * Parses comma-separated URLs and returns an array of trimmed, non-empty strings
 * Also handles newline separators for better UX
 */
export const parseListInput = (input: string): string[] => {
  if (!input || typeof input !== 'string') return []

  return input
    .split(/[,\n]/) // Split on comma or newline
    .map(item => item.trim()) // Trim whitespace
    .filter(item => item.length > 0) // Remove empty strings
}

// Keep the old name for backward compatibility
export const parseCommaSeparatedUrls = parseListInput

/**
 * Validates an array of URLs and returns categorized results
 */
export interface BulkUrlValidationResult {
  valid: string[]
  invalid: string[]
  duplicates: string[]
  cacConflicts: string[]
}

export const validateBulkUrls = (
  urls: string[],
  existingUrls: string[] = [],
  cacAllowedDomains: CACServices,
): BulkUrlValidationResult => {
  const valid: string[] = []
  const invalid: string[] = []
  const duplicates: string[] = []
  const cacConflicts: string[] = []
  const seen = new Set<string>()

  // Create a set of existing URLs for duplicate checking (case-insensitive)
  const existingSet = new Set(existingUrls.map(url => url.toLowerCase()))

  urls.forEach(url => {
    const lowerCaseUrl = url.toLowerCase()

    // Check for duplicates within the input
    if (seen.has(lowerCaseUrl)) {
      duplicates.push(url)
      return
    }

    // Check for duplicates against existing URLs
    if (existingSet.has(lowerCaseUrl)) {
      duplicates.push(url)
      return
    }

    // Validate the URL
    const {isValid} = checkValidUrlOrIPAddress(url)
    if (!isValid) {
      invalid.push(url)
      return
    }

    // Check against CAC allowed domains
    const cacCheck = checkDomainAgainstCAC(url, cacAllowedDomains)
    if (cacCheck.isMatch) {
      cacConflicts.push(url)
      return
    }

    // If all checks pass, add to valid list
    valid.push(url)
    seen.add(lowerCaseUrl)
  })

  return {valid, invalid, duplicates, cacConflicts}
}

export const validateIPv4 = (ip: string): boolean => {
  return ipV4ExactRegEx.test(ip)
}

export const stringSplitAndJoin = (
  str: string,
  splitBy: string = ' ',
  joinBy: string = '-',
): string => {
  return str?.split(splitBy)?.join(joinBy)
}

export const prefetchTranslations = async (ns: string[]): Promise<void> => {
  await i18next.loadNamespaces(ns)
}

export const sortAlphabetically = (list: string[]): string[] => {
  return list.sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1))
}

export const sortByDate = (list: CreatedDateItem[]): CreatedDateItem[] => {
  if (!list?.length) return []
  else if (list.length === 1) return list
  return list.sort((a: CreatedDateItem, b: CreatedDateItem) => {
    return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
  })
}

export const isJWTTokenExpired = (token: string): boolean => {
  const decodedToken: TokenDataType = jwtDecode(token)
  const tokenExp = new Date(decodedToken.exp * 1000)

  return tokenExp < new Date()
}

export const checkURLHasPath = (url: string): boolean => {
  // Remove protocol if present
  const urlWithoutProtocol = url.replace(/^https?:\/\//, '')

  // Check if there's a slash
  if (!urlWithoutProtocol.includes('/')) {
    return false
  }

  // Split by slash and filter out empty strings to handle multiple consecutive slashes
  const parts = urlWithoutProtocol.split('/').filter(part => part !== '')

  // Should have at least 2 parts: domain and path
  if (parts.length <= 1) {
    return false
  }

  // Check if the second part (after domain) starts with ? or # (query/fragment only)
  // If so, it means there's no actual path content, just query params or fragments
  const pathPart = parts[1]
  if (pathPart.startsWith('?') || pathPart.startsWith('#')) {
    return false
  }

  return true
}

// Check if the input URL domain matches given domain bypass item
export const checkDomainBypassMatch = (
  bypassDomain: string,
  inputDomain: string,
): boolean => {
  const bypassLower = bypassDomain.toLowerCase()
  const inputLower = inputDomain.toLowerCase()

  // Exact match
  if (bypassLower === inputLower) {
    return true
  }

  // Handle wildcard domains (e.g., *.test.com should match www.test.com)
  if (bypassLower.startsWith('*.')) {
    const baseDomain = bypassLower.substring(2) // Remove '*.'
    // Check if input domain ends with the base domain
    if (inputLower.endsWith('.' + baseDomain) || inputLower === baseDomain) {
      return true
    }
  }

  // Handle case where input has www. prefix and bypass is wildcard
  if (inputLower.startsWith('www.')) {
    const inputWithoutWww = inputLower.substring(4) // Remove 'www.'
    if (bypassLower === '*.' + inputWithoutWww) {
      return true
    }
  }

  // Handle IPv6 addresses - normalize brackets
  const normalizeIPv6 = (ip: string): string => {
    // Remove brackets if present
    const normalized = ip.replaceAll(/(^\[|\]$)/g, '')
    return normalized.toLowerCase()
  }

  // Check if both are IPv6 addresses
  if (
    ipV6WithBracketsRegEx.test(bypassDomain) &&
    ipV6WithBracketsRegEx.test(inputDomain)
  ) {
    return normalizeIPv6(bypassDomain) === normalizeIPv6(inputDomain)
  }

  return false
}
